import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../lib/auth';
import {
  RefreshCw, Search, Calendar, Building2, User, Clock, Shield, Route,
  Eye, Download, Trash2, Zap, Camera, FileText
} from 'lucide-react';

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('en-AU');
  } catch {
    return v;
  }
};

const fmtTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return v;
  }
};

const normRows = (data) => Array.isArray(data) ? data : (data?.items || data?.jobs || data?.data || data?.shifts || data?.results || []);

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('patrols');
  const [loading, setLoading] = useState(false);

  const today = new Date();
  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  const [dateFrom, setDateFrom] = useState(lastWeek.toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(today.toISOString().split('T')[0]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedOfficer, setSelectedOfficer] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [sites, setSites] = useState([]);
  const [officers, setOfficers] = useState([]);

  const [patrolReports, setPatrolReports] = useState([]);
  const [shiftReports, setShiftReports] = useState([]);
  const [incidentReports, setIncidentReports] = useState([]);
  const [adhocReports, setAdhocReports] = useState([]);
  const [intel, setIntel] = useState(null);

  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetailLoading, setReportDetailLoading] = useState(false);

  // RAPID X V6.8B — TEST REPORT DELETION
  // Stores "<type>:<id>" while one report is being deleted.
  const [deletingReportKey, setDeletingReportKey] = useState(null);

  const openPatrolReport = async (report) => {
    const runId = report?.id || report?._id;

    if (!runId) {
      toast.error('Unable to open patrol report: missing report ID');
      return;
    }

    setSelectedReport({ ...report, type: 'patrol' });
    setReportDetailLoading(true);

    try {
      const res = await api.get(`/reports/patrol/${runId}/data`);
      const detail = res?.data?.data || res?.data || {};

      setSelectedReport({
        ...report,
        ...detail,
        id: detail.id || runId,
        type: 'patrol',
      });
    } catch (error) {
      console.error('Failed to load patrol report details:', error);
      toast.error(
        error?.response?.data?.detail ||
        'Failed to load the complete patrol report'
      );
    } finally {
      setReportDetailLoading(false);
    }
  };

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    loadReports();
  }, [activeTab, dateFrom, dateTo, selectedSite, selectedOfficer]);

  const params = () => {
    const p = new URLSearchParams();
    if (dateFrom) p.append('from_date', dateFrom);
    if (dateTo) p.append('to_date', dateTo);
    if (selectedSite !== 'all') p.append('site_id', selectedSite);
    if (selectedOfficer !== 'all') p.append('officer_id', selectedOfficer);
    return p.toString();
  };

  const loadFilters = async () => {
    try {
      const [sRes, uRes] = await Promise.allSettled([
        api.get('/sites?limit=500'),
        api.get('/users?limit=500'),
      ]);

      if (sRes.status === 'fulfilled') setSites(normRows(sRes.value.data));
      if (uRes.status === 'fulfilled') {
        setOfficers(normRows(uRes.value.data).filter(u => ['security_officer', 'supervisor'].includes(u.role)));
      }
    } catch {}
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const q = params();

      if (activeTab === 'patrols') {
        const [legacyRes, routeRes] = await Promise.allSettled([
          api.get(`/patrols/runs?${q}&limit=100`),
          api.get(`/route-runs?${q}&limit=100`),
        ]);

        const legacy = legacyRes.status === 'fulfilled' ? normRows(legacyRes.value.data) : [];
        const routeRuns = routeRes.status === 'fulfilled' ? normRows(routeRes.value.data) : [];

        const adapted = routeRuns.map(rr => {
          const stops = rr.stops || [];
          const done = stops.filter(s => ['checked_in', 'completed'].includes(s.status)).length;
          return {
            id: rr.id,
            route_name: rr.route_name || 'Patrol Route',
            site_name: rr.site_name,
            officer_name: rr.guard_name,
            status: rr.status,
            completed_at: rr.completed_at || rr.end_time || rr.created_at,
            started_at: rr.start_time || rr.created_at,
            total_checkpoints: stops.length,
            checkpoints_completed: done,
            _source: 'route_runs',
          };
        });

        setPatrolReports([...adapted, ...legacy]);
      }

      if (activeTab === 'shifts') {
        const res = await api.get(`/shifts?${q}&status=completed&limit=100`);
        setShiftReports(normRows(res.data));
      }

      if (activeTab === 'incidents') {
        const res = await api.get(`/incidents?${q}&limit=100`);
        setIncidentReports(normRows(res.data));
      }

      if (activeTab === 'adhoc') {
        const res = await api.get(`/adhoc-jobs?${q}&limit=200`);
        const rows = normRows(res.data).filter(j => ['completed', 'cancelled'].includes(String(j.status || '').toLowerCase()));
        setAdhocReports(rows);
      }

      if (activeTab === 'intelligence') {
        // RAPID X V6.5 — OPERATIONS INTELLIGENCE FILTERS
        //
        // Use the same active reporting period and selections as the other
        // report tabs. The backend endpoint rejects incomplete analytics
        // requests with HTTP 400.
        const intelligenceQuery = params();

        const res = await api.get(
          `/adhoc-jobs/operations-intelligence${
            intelligenceQuery
              ? `?${intelligenceQuery}`
              : ''
          }`
        );

        setIntel(res.data);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const filtered = (rows, fields) => {
    if (!searchQuery) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter(r => fields.some(f => String(r?.[f] || '').toLowerCase().includes(q)));
  };

  const patrols = useMemo(() => filtered(patrolReports, ['route_name', 'site_name', 'officer_name', 'status']), [patrolReports, searchQuery]);
  const shifts = useMemo(() => filtered(shiftReports, ['site_name', 'officer_name', 'user_name', 'status']), [shiftReports, searchQuery]);
  const incidents = useMemo(() => filtered(incidentReports, ['incident_number', 'site_name', 'reported_by_name', 'description', 'status']), [incidentReports, searchQuery]);
  const adhoc = useMemo(() => filtered(adhocReports, ['job_number', 'site_name', 'client_name', 'assigned_to_name', 'instructions', 'closeout_notes', 'status']), [adhocReports, searchQuery]);

  const exportPdf = async (report, type) => {
    try {
      let endpoint = '';

      if (type === 'patrol') {
        endpoint = `/reports/patrol/${report.id}/pdf`;
      }

      if (type === 'shift') {
        endpoint = `/reports/shift/${report.id}/pdf`;
      }

      if (type === 'incident') {
        endpoint = `/reports/incident/${report.id}/pdf`;
      }

      if (!endpoint) {
        toast.info(
          'PDF export for this report type is next phase'
        );
        return;
      }

      const res = await api.get(endpoint, {
        responseType: 'blob',
      });

      const blob = new Blob(
        [res.data],
        { type: 'application/pdf' }
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      const cleanFilePart = (value, fallback) => {
        const text = String(value || fallback || '')
          .trim()
          .replace(/[\\/:*?"<>|]+/g, '-')
          .replace(/\s+/g, ' ')
          .replace(/-+/g, '-');

        return text || fallback;
      };

      const formatFileDate = (value) => {
        const date = value ? new Date(value) : new Date();

        if (Number.isNaN(date.getTime())) {
          return new Date()
            .toLocaleDateString('en-AU')
            .replaceAll('/', '.');
        }

        return date
          .toLocaleDateString('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
          .replaceAll('/', '.');
      };

      const siteName = cleanFilePart(
        report.site_name ||
        report.location_name ||
        report.route_name,
        'Site'
      );

      const reportDate = formatFileDate(
        report.completed_at ||
        report.end_time ||
        report.incident_time ||
        report.created_at ||
        report.started_at ||
        report.start_time
      );

      let downloadName = `Report - ${siteName} - ${reportDate}.pdf`;

      if (type === 'patrol') {
        downloadName =
          `Patrol Report - ${siteName} - ${reportDate}.pdf`;
      }

      if (type === 'shift') {
        downloadName =
          `Shift Report - ${siteName} - ${reportDate}.pdf`;
      }

      if (type === 'incident') {
        const incidentReference = cleanFilePart(
          report.incident_number ||
          report.reference_number ||
          report.id,
          'Incident'
        );

        downloadName =
          `Incident Report - ${siteName} - ` +
          `${reportDate} - ${incidentReference}.pdf`;
      }

      link.href = url;
      link.download = downloadName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        '[Rapid X V6.5] PDF export failed',
        error
      );

      toast.error('Failed to export PDF');
    }
  };

  // ==========================================================
  // RAPID X V6.8B — TEST REPORT DELETION
  // ==========================================================
  // Reports are generated from their source records:
  //   patrol  -> patrol_runs or route_runs
  //   shift   -> shifts
  //   incident -> incidents
  //
  // The configured API client already has /api as its base URL,
  // so these paths intentionally begin with /reports rather than
  // /api/reports.
  const handleDeleteReport = async (report, type) => {
    const reportId = report?.id || report?._id;

    if (!reportId) {
      toast.error('Unable to delete report: missing report ID');
      return;
    }

    const labels = {
      patrol: 'patrol report',
      shift: 'shift report',
      incident: 'incident report',
    };

    const reportLabel = labels[type];

    if (!reportLabel) {
      toast.error('Deletion is not available for this report type');
      return;
    }

    const endpoints = {
      patrol: `/reports/patrol/${reportId}`,
      shift: `/reports/shift/${reportId}`,
      incident: `/reports/incident/${reportId}`,
    };

    const displayName =
      report?.route_name ||
      report?.site_name ||
      report?.incident_number ||
      report?.reference_number ||
      reportLabel;

    const warning =
      type === 'shift'
        ? [
            `Delete the test shift report for "${displayName}"?`,
            '',
            'This may permanently remove the underlying test shift record.',
            'Only proceed if this is test data.',
          ].join('\n')
        : type === 'patrol'
        ? [
            `Delete the test patrol report for "${displayName}"?`,
            '',
            'This permanently removes the underlying test patrol run.',
            'Only proceed if this is test data.',
          ].join('\n')
        : [
            `Delete the test incident report "${displayName}"?`,
            '',
            'The backend may soft-delete the incident record.',
            'Only proceed if this is test data.',
          ].join('\n');

    if (!window.confirm(warning)) {
      return;
    }

    const deleteKey = `${type}:${reportId}`;
    setDeletingReportKey(deleteKey);

    try {
      const response = await api.delete(endpoints[type]);

      const backendMessage =
        response?.data?.message ||
        response?.data?.detail;

      toast.success(
        backendMessage ||
        `${
          reportLabel.charAt(0).toUpperCase() +
          reportLabel.slice(1)
        } deleted`
      );

      if (
        selectedReport &&
        String(selectedReport?.id || selectedReport?._id) ===
          String(reportId)
      ) {
        setSelectedReport(null);
      }

      await loadReports();
    } catch (error) {
      console.error(
        '[Rapid X V6.8B] Report deletion failed',
        {
          type,
          reportId,
          endpoint: endpoints[type],
          error,
        }
      );

      toast.error(
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        `Failed to delete ${reportLabel}`
      );
    } finally {
      setDeletingReportKey(null);
    }
  };


  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Reports</h1>
          <p className="text-zinc-400">View, export, and analyse patrol, shift, incident and adhoc response reports</p>
        </div>
        <Button variant="outline" onClick={loadReports}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <FilterInput label="From Date" type="date" value={dateFrom} onChange={setDateFrom} />
            <FilterInput label="To Date" type="date" value={dateTo} onChange={setDateTo} />

            <div>
              <label className="text-xs text-zinc-400">Site</label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map(s => <SelectItem key={s.id || s._id} value={s.id || s._id}>{s.name || s.site_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs text-zinc-400">Officer</label>
              <Select value={selectedOfficer} onValueChange={setSelectedOfficer}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Officers</SelectItem>
                  {officers.map(o => <SelectItem key={o.id || o._id} value={o.id || o._id}>{`${o.first_name || ''} ${o.last_name || ''}`.trim() || o.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs text-zinc-400">Search</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-500" />
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search..." className="pl-9 bg-zinc-950 border-zinc-700" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800 flex flex-wrap h-auto">
          <Tab value="patrols" icon={<Route />} label={`Patrol Reports (${patrols.length})`} />
          <Tab value="shifts" icon={<Clock />} label={`Shift Reports (${shifts.length})`} />
          <Tab value="incidents" icon={<Shield />} label={`Incident Reports (${incidents.length})`} />
          <Tab value="adhoc" icon={<Zap />} label={`Adhoc Reports (${adhoc.length})`} />
          <Tab value="intelligence" icon={<FileText />} label="Operations Intelligence" />
        </TabsList>

        <TabsContent value="patrols">
          <ReportList loading={loading} empty="No patrol reports found">
            {patrols.map(r => (
              <BasicReportCard
                key={r.id}
                title={r.route_name || 'Patrol Report'}
                status={r.status}
                meta={[
                  r.site_name,
                  r.officer_name || 'Unknown Officer',
                  fmtDate(r.completed_at || r.started_at),
                  `${r.checkpoints_completed || 0}/${r.total_checkpoints || 0} checkpoints`
                ]}
                onView={() => openPatrolReport(r)}
                onPdf={() => exportPdf(r, 'patrol')}
                onDelete={() => handleDeleteReport(r, 'patrol')}
                deleting={
                  deletingReportKey ===
                  `patrol:${r.id || r._id}`
                }
              />
            ))}
          </ReportList>
        </TabsContent>

        <TabsContent value="shifts">
          <ReportList loading={loading} empty="No shift reports found">
            {shifts.map(r => (
              <BasicReportCard
                key={r.id}
                title={r.site_name || 'Shift Report'}
                status={r.status}
                meta={[
                  r.officer_name || r.user_name || 'Unknown Officer',
                  fmtDate(r.completed_at || r.end_time),
                  fmtTime(r.start_time),
                  fmtTime(r.end_time)
                ]}
                onView={() =>
                  setSelectedReport({ ...r, type: 'shift' })
                }
                onPdf={() => exportPdf(r, 'shift')}
                onDelete={() => handleDeleteReport(r, 'shift')}
                deleting={
                  deletingReportKey ===
                  `shift:${r.id || r._id}`
                }
              />
            ))}
          </ReportList>
        </TabsContent>

        <TabsContent value="incidents">
          <ReportList loading={loading} empty="No incident reports found">
            {incidents.map(r => (
              <BasicReportCard
                key={r.id}
                title={r.incident_number || 'Incident Report'}
                status={r.status}
                meta={[
                  r.site_name,
                  r.reported_by_name || 'Unknown Officer',
                  fmtDate(r.created_at),
                  r.severity || ''
                ]}
                onView={() =>
                  setSelectedReport({ ...r, type: 'incident' })
                }
                onPdf={() => exportPdf(r, 'incident')}
                onDelete={() => handleDeleteReport(r, 'incident')}
                deleting={
                  deletingReportKey ===
                  `incident:${r.id || r._id}`
                }
              />
            ))}
          </ReportList>
        </TabsContent>

        <TabsContent value="adhoc">
          <ReportList loading={loading} empty="No adhoc response reports found">
            {adhoc.map(j => (
              <AdhocCard key={j.id} job={j} onView={() => setSelectedReport({ ...j, type: 'adhoc' })} />
            ))}
          </ReportList>
        </TabsContent>

        <TabsContent value="intelligence">
          <IntelligencePanel loading={loading} intel={intel} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedReport} onOpenChange={(v) => !v && setSelectedReport(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedReport?.job_number || selectedReport?.route_name || selectedReport?.incident_number || 'Report Details'}</DialogTitle>
          </DialogHeader>
          {selectedReport?.type === 'adhoc' ? (
            <AdhocDetail job={selectedReport} />
          ) : selectedReport?.type === 'patrol' ? (
            reportDetailLoading ? (
              <div className="flex items-center justify-center py-16 text-zinc-400">
                <RefreshCw className="w-5 h-5 mr-3 animate-spin" />
                Loading complete patrol report…
              </div>
            ) : (
              <PatrolReportDetail report={selectedReport} />
            )
          ) : selectedReport?.type === 'shift' ? (
            <ShiftReportDetail report={selectedReport} />
          ) : (
            <pre className="text-xs bg-zinc-950 p-4 rounded overflow-auto max-h-[70vh]">
              {JSON.stringify(selectedReport, null, 2)}
            </pre>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



function AuthenticatedReportPhoto({ source, alt }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = null;

    const loadPhoto = async () => {
      try {
        setFailed(false);

        if (
          source.startsWith('http://') ||
          source.startsWith('https://') ||
          source.startsWith('data:') ||
          source.startsWith('blob:')
        ) {
          if (active) setBlobUrl(source);
          return;
        }

        // The configured API client already uses /api as its base path.
        const apiPath = source.startsWith('/api/')
          ? source.slice(4)
          : source;

        const response = await api.get(apiPath, {
          responseType: 'blob',
        });

        objectUrl = URL.createObjectURL(response.data);

        if (active) {
          setBlobUrl(objectUrl);
        }
      } catch (error) {
        console.error('Failed to load patrol photo:', source, error);

        if (active) {
          setFailed(true);
        }
      }
    };

    loadPhoto();

    return () => {
      active = false;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [source]);

  if (failed) {
    return (
      <div className="flex h-32 items-center justify-center rounded border border-zinc-800 bg-zinc-900 px-3 text-center text-xs text-red-400">
        Photo unavailable
      </div>
    );
  }

  if (!blobUrl) {
    return (
      <div className="flex h-32 items-center justify-center rounded border border-zinc-800 bg-zinc-900 text-xs text-zinc-500">
        Loading photo…
      </div>
    );
  }

  return (
    <a
      href={blobUrl}
      target="_blank"
      rel="noreferrer"
      className="block overflow-hidden rounded border border-zinc-800 bg-zinc-900"
    >
      <img
        src={blobUrl}
        alt={alt}
        className="h-32 w-full object-cover"
        loading="lazy"
      />
    </a>
  );
}

function PatrolReportDetail({ report }) {
  const scans = Array.isArray(report?.checkpoint_scans)
    ? report.checkpoint_scans
    : [];

  const completed = Number(
    report?.checkpoints_completed ?? scans.length ?? 0
  );

  const total = Number(
    report?.total_checkpoints ?? scans.length ?? 0
  );

  const percentage = Number(
    report?.completion_percentage ??
    (total > 0 ? Math.round((completed / total) * 100) : 0)
  );

  const statusLabel = String(report?.status || 'unknown')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

  const dateTime = (value) => {
    if (!value) return '—';

    try {
      return new Date(value).toLocaleString('en-AU', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return value;
    }
  };

  const photoSource = (photo) => {
    if (!photo) return null;

    const source =
      typeof photo === 'string'
        ? photo
        : photo.url || photo.file_url || photo.path || photo.filename;

    if (!source) return null;

    if (
      source.startsWith('http://') ||
      source.startsWith('https://') ||
      source.startsWith('data:') ||
      source.startsWith('blob:')
    ) {
      return source;
    }

    return source.startsWith('/') ? source : `/${source}`;
  };

  return (
    <div className="space-y-5 max-h-[72vh] overflow-y-auto pr-1">
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-500">
              Patrol verification report
            </div>

            <h3 className="mt-1 text-xl font-semibold text-zinc-100">
              {report?.route_name || 'Patrol Report'}
            </h3>

            <div className="mt-1 text-sm text-zinc-400">
              {report?.client?.name || 'Client not recorded'}
              {' · '}
              {report?.site?.name || 'Site not recorded'}
            </div>
          </div>

          <Badge
            variant="outline"
            className={
              report?.has_exceptions
                ? 'border-amber-500/50 bg-amber-500/10 text-amber-300'
                : 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
            }
          >
            {report?.has_exceptions ? 'Exceptions recorded' : statusLabel}
          </Badge>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <ReportMetric
          icon={<User />}
          label="Officer"
          value={report?.officer?.name || 'Unknown officer'}
        />

        <ReportMetric
          icon={<Clock />}
          label="Duration"
          value={`${Number(report?.duration_minutes || 0)} minutes`}
        />

        <ReportMetric
          icon={<Shield />}
          label="Checkpoints"
          value={`${completed} of ${total}`}
        />

        <ReportMetric
          icon={<Route />}
          label="Completion"
          value={`${percentage}%`}
        />
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
        <h4 className="mb-3 font-medium text-zinc-200">Patrol timeline</h4>

        <div className="grid gap-3 sm:grid-cols-3">
          <ReportField
            label="Started"
            value={dateTime(report?.started_at)}
          />

          <ReportField
            label="Completed"
            value={dateTime(report?.completed_at)}
          />

          <ReportField
            label="Stopped"
            value={dateTime(report?.stopped_at)}
          />
        </div>
      </div>

      {(report?.stop_reason || report?.stop_notes) && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
          <h4 className="font-medium text-amber-300">
            Patrol stopped or exception recorded
          </h4>

          {report?.stop_reason && (
            <p className="mt-2 text-sm text-zinc-200">
              <span className="text-zinc-400">Reason: </span>
              {report.stop_reason}
            </p>
          )}

          {report?.stop_notes && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-300">
              {report.stop_notes}
            </p>
          )}
        </div>
      )}

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h4 className="font-medium text-zinc-200">Checkpoint activity</h4>

          <span className="text-xs text-zinc-500">
            {scans.length} scan{scans.length === 1 ? '' : 's'}
          </span>
        </div>

        {scans.length === 0 ? (
          <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-6 text-center text-sm text-zinc-500">
            No checkpoint scans were recorded for this patrol.
          </div>
        ) : (
          <div className="space-y-3">
            {scans.map((scan, index) => {
              const photos = Array.isArray(scan?.photos)
                ? scan.photos
                : [];

              return (
                <div
                  key={`${scan?.checkpoint_id || 'checkpoint'}-${index}`}
                  className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-sm font-semibold text-blue-300">
                        {index + 1}
                      </div>

                      <div className="min-w-0">
                        <div className="font-medium text-zinc-100">
                          {scan?.checkpoint_name || `Checkpoint ${index + 1}`}
                        </div>

                        <div className="mt-1 text-xs text-zinc-500">
                          {dateTime(scan?.scanned_at)}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Badge
                        variant="outline"
                        className="border-zinc-700 text-zinc-300"
                      >
                        {scan?.result || 'SUCCESS'}
                      </Badge>

                      <Badge
                        variant="outline"
                        className={
                          scan?.gps_verified
                            ? 'border-emerald-500/40 text-emerald-300'
                            : 'border-zinc-700 text-zinc-400'
                        }
                      >
                        {scan?.gps_verified ? 'GPS verified' : 'No GPS'}
                      </Badge>
                    </div>
                  </div>

                  {scan?.notes && (
                    <div className="mt-3 rounded bg-zinc-900 p-3 text-sm text-zinc-300 whitespace-pre-wrap">
                      {scan.notes}
                    </div>
                  )}

                  {scan?.latitude && scan?.longitude && (
                    <div className="mt-3 text-xs text-zinc-500">
                      GPS: {scan.latitude}, {scan.longitude}
                    </div>
                  )}

                  {photos.length > 0 && (
                    <div className="mt-4">
                      <div className="mb-2 flex items-center text-xs text-zinc-400">
                        <Camera className="mr-2 h-4 w-4" />
                        {photos.length} photo{photos.length === 1 ? '' : 's'}
                      </div>

                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {photos.map((photo, photoIndex) => {
                          const src = photoSource(photo);

                          if (!src) return null;

                          return (
                            <AuthenticatedReportPhoto
                              key={`${index}-${photoIndex}`}
                              source={src}
                              alt={`${scan?.checkpoint_name || 'Checkpoint'} photo ${photoIndex + 1}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ReportMetric({ icon, label, value }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-zinc-500">
        {React.cloneElement(icon, { className: 'h-4 w-4' })}
        {label}
      </div>

      <div className="mt-2 text-sm font-medium text-zinc-100">
        {value || '—'}
      </div>
    </div>
  );
}

function ReportField({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-zinc-500">
        {label}
      </div>

      <div className="mt-1 text-sm text-zinc-200">
        {value || '—'}
      </div>
    </div>
  );
}

function FilterInput({ label, type, value, onChange }) {
  return (
    <div>
      <label className="text-xs text-zinc-400">{label}</label>
      <Input type={type} value={value} onChange={e => onChange(e.target.value)} className="bg-zinc-950 border-zinc-700" />
    </div>
  );
}

function Tab({ value, icon, label }) {
  return (
    <TabsTrigger value={value} className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
      {React.cloneElement(icon, { className: 'w-4 h-4 mr-2' })}
      {label}
    </TabsTrigger>
  );
}

function ReportList({ loading, empty, children }) {
  if (loading) return <div className="py-12 text-center text-zinc-400">Loading reports...</div>;
  if (!children || (Array.isArray(children) && children.length === 0)) {
    return <Card className="bg-zinc-900 border-zinc-800"><CardContent className="py-12 text-center text-zinc-400">{empty}</CardContent></Card>;
  }
  return <div className="space-y-3">{children}</div>;
}

function BasicReportCard({
  title,
  status,
  meta,
  onView,
  onPdf,
  onDelete,
  deleting = false,
}) {
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-white">{title}</h3>
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">{status || 'Completed'}</Badge>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-400">
            {meta.filter(Boolean).map((m, i) => <span key={i}>{m}</span>)}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onView}><Eye className="w-4 h-4 mr-2" /> View</Button>
          <Button variant="outline" size="sm" onClick={onPdf}><Download className="w-4 h-4 mr-2" /> PDF</Button>
          <Button
            variant="outline"
            size="sm"
            className="border-red-700 text-red-400 hover:bg-red-950/40 hover:text-red-300"
            onClick={onDelete}
            disabled={deleting || !onDelete}
          >
            {deleting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Deleting…
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AdhocCard({ job, onView }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="p-4">
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="font-mono font-semibold text-amber-400">{job.job_number}</h3>
              <Badge className={String(job.status).toLowerCase() === 'completed' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}>{job.status}</Badge>
              <Badge variant="outline" className="border-zinc-700">{job.priority || 'normal'}</Badge>
              <Badge variant="outline" className="border-zinc-700">{job.job_type || 'Adhoc Response'}</Badge>
            </div>
            <div className="text-sm text-zinc-400 flex flex-wrap gap-x-4 gap-y-1">
              <span><Building2 className="w-4 h-4 inline mr-1" />{job.site_name || 'Unknown Site'}</span>
              <span><User className="w-4 h-4 inline mr-1" />{job.assigned_to_name || 'Unknown Officer'}</span>
              <span><Calendar className="w-4 h-4 inline mr-1" />{fmtDate(job.completed_at || job.created_at)}</span>
              <span><Camera className="w-4 h-4 inline mr-1" />{job.photos_count || 0} photos</span>
            </div>
            {job.closeout_notes && (
              <div className="mt-3 bg-emerald-950/30 border border-emerald-900/60 rounded-lg p-2 text-sm text-emerald-300">
                <strong>Closeout:</strong> {job.closeout_notes}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 min-w-[360px]">
            <Metric label="Response" value={job.response_time} />
            <Metric label="Travel" value={job.travel_time} />
            <Metric label="On Site" value={job.time_on_site} />
            <Metric label="Total" value={job.total_job_time} />
          </div>
          <Button variant="outline" size="sm" onClick={onView}><Eye className="w-4 h-4 mr-2" /> View</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }) {
  return <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-2"><div className="text-[11px] text-zinc-500">{label}</div><div className="font-mono text-sm">{value || '—'}</div></div>;
}


function ShiftReportDetail({ report }) {
  if (!report) return null;

  const formatLabel = (value, fallback = 'Not recorded') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    return String(value)
      .replaceAll('_', ' ')
      .replace(/\b\w/g, letter => letter.toUpperCase());
  };

  const formatDateTime = (value) => {
    if (!value) return 'Not recorded';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const formatTime = (value) => {
    if (!value) return 'Not recorded';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const startValue =
    report.actual_start ||
    report.sign_on_at ||
    null;

  const endValue =
    report.actual_end ||
    report.sign_off_at ||
    null;

  const calculateDuration = () => {
    if (
      report.duration_hours !== null &&
      report.duration_hours !== undefined
    ) {
      const decimalHours = Number(report.duration_hours);

      if (!Number.isNaN(decimalHours)) {
        const totalMinutes = Math.round(decimalHours * 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return `${hours}h ${minutes}m`;
      }
    }

    if (!startValue || !endValue) {
      return 'Not available';
    }

    const startDate = new Date(startValue);
    const endDate = new Date(endValue);

    if (
      Number.isNaN(startDate.getTime()) ||
      Number.isNaN(endDate.getTime())
    ) {
      return 'Not available';
    }

    const totalMinutes = Math.max(
      0,
      Math.round(
        (endDate.getTime() - startDate.getTime()) / 60000
      )
    );

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  const calculateStartVariance = () => {
    if (!report.start_time || !startValue) {
      return {
        minutes: null,
        label: 'Not available',
        status: 'neutral',
      };
    }

    const scheduled = new Date(report.start_time);
    const actual = new Date(startValue);

    if (
      Number.isNaN(scheduled.getTime()) ||
      Number.isNaN(actual.getTime())
    ) {
      return {
        minutes: null,
        label: 'Not available',
        status: 'neutral',
      };
    }

    const minutes = Math.round(
      (actual.getTime() - scheduled.getTime()) / 60000
    );

    if (minutes > 0) {
      return {
        minutes,
        label: `${minutes} minute${minutes === 1 ? '' : 's'} late`,
        status: 'late',
      };
    }

    if (minutes < 0) {
      const earlyMinutes = Math.abs(minutes);

      return {
        minutes,
        label: `${earlyMinutes} minute${earlyMinutes === 1 ? '' : 's'} early`,
        status: 'early',
      };
    }

    return {
      minutes: 0,
      label: 'Started on time',
      status: 'on_time',
    };
  };

  const signOnGps = report.sign_on_gps || {};
  const signOffGps = report.sign_off_gps || {};
  const startVariance = calculateStartVariance();

  const statusLabel = formatLabel(report.status, 'Unknown');
  const shiftTypeLabel = formatLabel(
    report.shift_type,
    'Not recorded'
  );

  const timelineEvents = [
    {
      label: 'Shift Accepted',
      value:
        report.accepted_at ||
        report.acceptance_at ||
        report.created_at,
      complete: report.acceptance_status === 'accepted',
    },
    {
      label: 'SOP Acknowledged',
      value: report.sop_acknowledged_at,
      complete: Boolean(report.sop_acknowledged),
    },
    {
      label: 'Fit for Work Confirmed',
      value: report.fit_for_work_at,
      complete: Boolean(report.fit_for_work),
    },
    {
      label: 'Officer Signed On',
      value: startValue,
      complete: Boolean(startValue),
    },
    {
      label: 'Officer Signed Off',
      value: endValue,
      complete: Boolean(endValue),
    },
    {
      label: 'Shift Completed',
      value: endValue || report.completed_at,
      complete: report.status === 'completed',
    },
  ];

  const complianceExceptions = [];

  if (!report.sop_acknowledged) {
    complianceExceptions.push('SOP was not acknowledged');
  }

  if (!report.fit_for_work) {
    complianceExceptions.push(
      'Fit-for-work declaration was not completed'
    );
  }

  if (report.acceptance_status !== 'accepted') {
    complianceExceptions.push('Shift was not formally accepted');
  }

  if (report.status !== 'completed') {
    complianceExceptions.push('Shift is not marked as completed');
  }

  if (
    startVariance.status === 'late' &&
    startVariance.minutes > 5
  ) {
    complianceExceptions.push(
      `Shift started ${startVariance.minutes} minutes late`
    );
  }

  const compliancePassed = complianceExceptions.length === 0;

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Shift Report
            </div>

            <h2 className="mt-2 text-xl font-semibold text-zinc-100">
              {report.site_name || 'Unknown Site'}
            </h2>

            <div className="mt-1 text-sm text-zinc-400">
              {report.site_code ? `${report.site_code} · ` : ''}
              {report.officer_name || 'Unknown Officer'}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-lg border border-zinc-700 px-3 py-2 text-xs font-medium text-zinc-200 hover:bg-zinc-800"
            >
              Print Report
            </button>

            <div className="w-fit rounded-full border border-emerald-900 bg-emerald-950/40 px-3 py-1 text-xs font-semibold text-emerald-300">
              {statusLabel}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`rounded-xl border p-4 ${
          compliancePassed
            ? 'border-emerald-900/70 bg-emerald-950/30'
            : 'border-amber-900/70 bg-amber-950/30'
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-bold ${
              compliancePassed
                ? 'bg-emerald-900/60 text-emerald-300'
                : 'bg-amber-900/60 text-amber-300'
            }`}
          >
            {compliancePassed ? '✓' : '!'}
          </div>

          <div>
            <div
              className={`text-xs font-semibold uppercase tracking-[0.16em] ${
                compliancePassed
                  ? 'text-emerald-400'
                  : 'text-amber-400'
              }`}
            >
              Overall Compliance
            </div>

            <div className="mt-1 text-lg font-semibold text-zinc-100">
              {compliancePassed
                ? 'Pass'
                : 'Pass With Exceptions'}
            </div>

            {complianceExceptions.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm text-amber-200">
                {complianceExceptions.map(exception => (
                  <li key={exception}>
                    • {exception}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <ShiftMetric
          label="Officer"
          value={report.officer_name || 'Not recorded'}
        />

        <ShiftMetric
          label="Shift Type"
          value={shiftTypeLabel}
        />

        <ShiftMetric
          label="Recorded Duration"
          value={calculateDuration()}
        />

        <div
          className={`rounded-xl border p-4 ${
            startVariance.status === 'late'
              ? 'border-amber-900 bg-amber-950/20'
              : startVariance.status === 'early' ||
                startVariance.status === 'on_time'
              ? 'border-emerald-900 bg-emerald-950/20'
              : 'border-zinc-800 bg-zinc-950'
          }`}
        >
          <div className="text-[11px] uppercase tracking-wide text-zinc-500">
            Start Variance
          </div>

          <div
            className={`mt-2 font-medium ${
              startVariance.status === 'late'
                ? 'text-amber-300'
                : startVariance.status === 'early' ||
                  startVariance.status === 'on_time'
                ? 'text-emerald-300'
                : 'text-zinc-100'
            }`}
          >
            {startVariance.label}
          </div>
        </div>
      </div>

      <ShiftSection title="Shift Timeline">
        <div className="relative ml-3 border-l border-zinc-700 pl-6">
          {timelineEvents.map((event, index) => (
            <div
              key={`${event.label}-${index}`}
              className="relative pb-5 last:pb-0"
            >
              <div
                className={`absolute -left-[31px] top-0 flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                  event.complete
                    ? 'border-emerald-500 bg-emerald-950'
                    : 'border-zinc-600 bg-zinc-900'
                }`}
              >
                {event.complete && (
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                )}
              </div>

              <div className="font-medium text-zinc-200">
                {event.label}
              </div>

              <div className="mt-1 text-xs text-zinc-500">
                {event.value
                  ? formatDateTime(event.value)
                  : 'Not recorded'}
              </div>
            </div>
          ))}
        </div>
      </ShiftSection>

      <ShiftSection title="Shift Timing">
        <div className="grid gap-3 md:grid-cols-2">
          <ShiftField
            label="Scheduled Start"
            value={formatDateTime(report.start_time)}
          />

          <ShiftField
            label="Scheduled End"
            value={formatDateTime(report.end_time)}
          />

          <ShiftField
            label="Actual Sign On"
            value={formatDateTime(startValue)}
          />

          <ShiftField
            label="Actual Sign Off"
            value={formatDateTime(endValue)}
          />
        </div>

        {startVariance.status !== 'neutral' && (
          <div
            className={`mt-3 rounded-lg border px-3 py-2 text-sm ${
              startVariance.status === 'late'
                ? 'border-amber-900/70 bg-amber-950/30 text-amber-300'
                : 'border-emerald-900/70 bg-emerald-950/30 text-emerald-300'
            }`}
          >
            {startVariance.status === 'late' ? '⚠ ' : '✓ '}
            Actual start was {startVariance.label}.
          </div>
        )}
      </ShiftSection>

      <ShiftSection title="Pre-Start and Completion">
        <div className="grid gap-3 md:grid-cols-2">
          <ShiftCheck
            label="SOP Acknowledged"
            passed={Boolean(report.sop_acknowledged)}
            detail={formatDateTime(report.sop_acknowledged_at)}
          />

          <ShiftCheck
            label="Fit for Work"
            passed={Boolean(report.fit_for_work)}
            detail={formatDateTime(report.fit_for_work_at)}
          />

          <ShiftCheck
            label="Shift Accepted"
            passed={report.acceptance_status === 'accepted'}
            detail={formatLabel(
              report.acceptance_status,
              'Not recorded'
            )}
          />

          <ShiftCheck
            label="Shift Completed"
            passed={report.status === 'completed'}
            detail={statusLabel}
          />
        </div>
      </ShiftSection>

      {(report.sign_on_signature_url ||
        report.sign_off_signature_url) && (
        <ShiftSection title="Officer Signatures">
          <div className="grid gap-4 md:grid-cols-2">
            {report.sign_on_signature_url && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Sign-On Signature
                  </div>

                  {signOnGps.latitude &&
                    signOnGps.longitude && (
                      <div className="rounded-full bg-emerald-950 px-2 py-1 text-[10px] font-semibold text-emerald-300">
                        GPS Verified
                      </div>
                    )}
                </div>

                <AuthenticatedShiftSignature
                  source={report.sign_on_signature_url}
                  alt="Officer sign-on signature"
                />

                <div className="mt-2 text-xs text-zinc-500">
                  Signed {formatDateTime(report.sign_on_at)}
                </div>
              </div>
            )}

            {report.sign_off_signature_url && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Sign-Off Signature
                  </div>

                  {signOffGps.latitude &&
                    signOffGps.longitude && (
                      <div className="rounded-full bg-emerald-950 px-2 py-1 text-[10px] font-semibold text-emerald-300">
                        GPS Verified
                      </div>
                    )}
                </div>

                <AuthenticatedShiftSignature
                  source={report.sign_off_signature_url}
                  alt="Officer sign-off signature"
                />

                <div className="mt-2 text-xs text-zinc-500">
                  Signed {formatDateTime(report.sign_off_at)}
                </div>
              </div>
            )}
          </div>
        </ShiftSection>
      )}

      {(signOnGps.latitude !== undefined ||
        signOnGps.longitude !== undefined) && (
        <ShiftGpsSection
          title="Sign-On Location"
          latitude={signOnGps.latitude}
          longitude={signOnGps.longitude}
        />
      )}

      {(signOffGps.latitude !== undefined ||
        signOffGps.longitude !== undefined) && (
        <ShiftGpsSection
          title="Sign-Off Location"
          latitude={signOffGps.latitude}
          longitude={signOffGps.longitude}
        />
      )}

      <ShiftSection title="Shift Notes">
        <div className="min-h-20 whitespace-pre-wrap rounded-lg border border-zinc-800 bg-zinc-950 p-4 text-sm text-zinc-200">
          {report.notes || 'No shift notes were recorded.'}
        </div>
      </ShiftSection>

      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-500">
        <div className="grid gap-2 md:grid-cols-3">
          <div>
            <span className="text-zinc-600">Report ID:</span>
            <div className="mt-1 break-all text-zinc-400">
              {report.id || report._id || 'Not available'}
            </div>
          </div>

          <div>
            <span className="text-zinc-600">Report Status:</span>
            <div className="mt-1 text-zinc-400">
              {statusLabel}
            </div>
          </div>

          <div>
            <span className="text-zinc-600">Viewed:</span>
            <div className="mt-1 text-zinc-400">
              {new Date().toLocaleString('en-AU', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShiftSection({ title, children }) {
  return (
    <section className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-zinc-300">
        {title}
      </h3>

      {children}
    </section>
  );
}

function ShiftMetric({ label, value }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">
        {label}
      </div>

      <div className="mt-2 font-medium text-zinc-100">
        {value}
      </div>
    </div>
  );
}

function ShiftField({ label, value }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">
        {label}
      </div>

      <div className="mt-1 text-sm text-zinc-200">
        {value}
      </div>
    </div>
  );
}

function ShiftCheck({ label, passed, detail }) {
  return (
    <div className="flex gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3">
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
          passed
            ? 'bg-emerald-950 text-emerald-300'
            : 'bg-red-950 text-red-300'
        }`}
      >
        {passed ? '✓' : '×'}
      </div>

      <div>
        <div className="font-medium text-zinc-200">
          {label}
        </div>

        {detail && (
          <div className="mt-1 text-xs text-zinc-500">
            {detail}
          </div>
        )}
      </div>
    </div>
  );
}

function ShiftGpsSection({ title, latitude, longitude }) {
  const hasCoordinates =
    latitude !== null &&
    latitude !== undefined &&
    longitude !== null &&
    longitude !== undefined;

  return (
    <ShiftSection title={title}>
      <div className="grid gap-3 md:grid-cols-2">
        <ShiftField
          label="Latitude"
          value={latitude ?? 'Not recorded'}
        />

        <ShiftField
          label="Longitude"
          value={longitude ?? 'Not recorded'}
        />
      </div>

      {hasCoordinates && (
        <a
          href={`https://www.google.com/maps?q=${encodeURIComponent(
            `${latitude},${longitude}`
          )}`}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex rounded-lg border border-zinc-700 px-3 py-2 text-xs font-medium text-zinc-200 hover:bg-zinc-800"
        >
          View location in Google Maps
        </a>
      )}
    </ShiftSection>
  );
}

function AuthenticatedShiftSignature({ source, alt }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loadingSignature, setLoadingSignature] = useState(true);

  useEffect(() => {
    let active = true;
    let objectUrl = null;

    const loadSignature = async () => {
      try {
        setFailed(false);
        setLoadingSignature(true);

        if (
          source.startsWith('http://') ||
          source.startsWith('https://') ||
          source.startsWith('data:') ||
          source.startsWith('blob:')
        ) {
          if (active) {
            setBlobUrl(source);
          }

          return;
        }

        const apiPath = source.startsWith('/api/')
          ? source.slice(4)
          : source;

        const response = await api.get(apiPath, {
          responseType: 'blob',
        });

        objectUrl = URL.createObjectURL(response.data);

        if (active) {
          setBlobUrl(objectUrl);
        }
      } catch (error) {
        console.error('Failed to load shift signature:', error);

        if (active) {
          setFailed(true);
        }
      } finally {
        if (active) {
          setLoadingSignature(false);
        }
      }
    };

    loadSignature();

    return () => {
      active = false;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [source]);

  if (loadingSignature) {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border border-zinc-800 bg-white text-sm text-zinc-500">
        Loading signature…
      </div>
    );
  }

  if (failed || !blobUrl) {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-zinc-700 bg-zinc-950 px-4 text-center text-sm text-zinc-500">
        Signature image could not be loaded.
      </div>
    );
  }

  return (
    <a
      href={blobUrl}
      target="_blank"
      rel="noreferrer"
      className="block overflow-hidden rounded-lg border border-zinc-800 bg-white p-3"
    >
      <img
        src={blobUrl}
        alt={alt}
        className="h-36 w-full object-contain"
      />
    </a>
  );
}

function AdhocDetail({ job }) {
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-4 gap-2">
        <Metric label="Response" value={job.response_time} />
        <Metric label="Travel" value={job.travel_time} />
        <Metric label="Time On Site" value={job.time_on_site} />
        <Metric label="Total Job Time" value={job.total_job_time} />
      </div>
      <div className="grid md:grid-cols-5 gap-2 text-sm">
        <Metric label="Dispatched" value={fmtTime(job.created_at)} />
        <Metric label="Accepted" value={fmtTime(job.accepted_at)} />
        <Metric label="En Route" value={fmtTime(job.en_route_at)} />
        <Metric label="On Site" value={fmtTime(job.on_site_at)} />
        <Metric label="Off Site" value={fmtTime(job.off_site_at || job.completed_at)} />
      </div>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
        <div className="text-xs text-zinc-500 mb-1">Closeout Notes</div>
        <div>{job.closeout_notes || '—'}</div>
      </div>
      <div className="text-xs text-zinc-500">
        Photos: {job.photos_count || 0} · GPS Accepted: {job.accepted_gps ? 'Yes' : 'No'} · GPS On Site: {job.on_site_gps ? 'Yes' : 'No'} · GPS Completed: {job.completed_gps ? 'Yes' : 'No'}
      </div>
    </div>
  );
}

function IntelligencePanel({ loading, intel }) {
  if (loading) return <div className="py-12 text-center text-zinc-400">Loading intelligence...</div>;
  const s = intel?.summary || {};
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Metric label="Total Jobs" value={s.total_jobs ?? '—'} />
        <Metric label="Active" value={s.active_jobs ?? '—'} />
        <Metric label="Completed" value={s.completed_jobs ?? '—'} />
        <Metric label="Cancelled" value={s.cancelled_jobs ?? '—'} />
        <Metric label="Avg Response" value={s.avg_response_time} />
        <Metric label="Avg Travel" value={s.avg_travel_time} />
        <Metric label="Avg On Site" value={s.avg_time_on_site} />
        <Metric label="SLA Breaches" value={s.sla_breaches ?? '—'} />
      </div>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3">Officer Performance</h3>
          {(intel?.officers || []).slice(0, 10).map(o => (
            <div key={o.id} className="grid grid-cols-5 gap-2 py-2 border-b border-zinc-800 text-sm">
              <div className="col-span-2">{o.name}</div>
              <div>{o.total_jobs} jobs</div>
              <div>{o.avg_response_time || '—'}</div>
              <div>{o.avg_time_on_site || '—'}</div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
